import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Dialog,
  Button,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Avatar,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Slider,
  TextField,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  AccountCircle as AccountIcon,
  Person as ProfileIcon,
  Palette as PaletteIcon,
  Videocam as VideocamIcon,
  Notifications as NotificationsIcon,
  ChatBubbleOutlineOutlined as MessagesIcon,
  Security as PrivacyIcon,
  Link as ConnectionsIcon,
  PlayArrow as PlayIcon,
  Tune as AdvancedIcon,
  WarningAmberOutlined as WarningIcon,
  QuickreplyOutlined as QuickReplyIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { themeMeta } from '../theme';
import { playMentionSound } from '../utils/notifySound';
import DeviceSettingsPanel from './DeviceSettingsPanel';
import MicProcessingPanel from './MicProcessingPanel';
import AvatarPickerDialog from './AvatarPickerDialog';
import { makeBuiltinAvatarSpec } from '../services/builtinAvatars';
import {
  FONT_SIZES,
  DEFAULT_FONT_SIZE,
  TIMESTAMP_FORMATS,
  DEFAULT_TIMESTAMP_FORMAT,
  DEFAULT_MENTION_VOLUME,
} from '../utils/displayPrefs';
import AvatarFallback from './AvatarFallback';
import ProfileView from './profile/ProfileView';
import { QUICK_REPLY_MAX, QUICK_REPLY_MAX_LEN } from './QuickRepliesPopover';

// Username-color options for the picker dropdown: { hex, name }. Names make the
// dropdown readable; the swatch + colored label still convey the color itself.
const USERNAME_COLOR_OPTIONS = [
  { hex: '#FF0000', name: 'Red' },
  { hex: '#FF6600', name: 'Orange' },
  { hex: '#FFCC00', name: 'Gold' },
  { hex: '#FFFF00', name: 'Yellow' },
  { hex: '#99CC00', name: 'Lime' },
  { hex: '#00FF00', name: 'Green' },
  { hex: '#00ff88', name: 'Mint' },
  { hex: '#008000', name: 'Forest' },
  { hex: '#008080', name: 'Teal' },
  { hex: '#00CCFF', name: 'Sky' },
  { hex: '#00d9ff', name: 'Cyan' },
  { hex: '#0000FF', name: 'Blue' },
  { hex: '#3366FF', name: 'Royal Blue' },
  { hex: '#000080', name: 'Navy' },
  { hex: '#800080', name: 'Purple' },
  { hex: '#FF00FF', name: 'Magenta' },
  { hex: '#FF99CC', name: 'Pink' },
  { hex: '#993300', name: 'Brown' },
  { hex: '#808080', name: 'Gray' },
  { hex: '#C0C0C0', name: 'Silver' },
  { hex: '#FFFFFF', name: 'White' },
];

// Colors reserved for ADMIN/OWNER — must match backend RESERVED_COLORS
// (roleColors.js). Compared case-insensitively. Hidden from non-staff pickers;
// the server also rejects them, so this is convenience, not the security boundary.
const RESERVED_COLORS = ['#ff0142', '#ff9900'];
const isReserved = (hex) =>
  RESERVED_COLORS.some((c) => c.toLowerCase() === (hex || '').toLowerCase());

// Profile bio length cap — mirrors the backend's 200-char limit (set-bio).
const PROFILE_BIO_MAX = 200;

// Banner-color swatches for the profile hero background. Same named palette as
// the username colors (reserved staff colors excluded — the banner is decorative,
// not a role signal). "Theme default" (empty value) is offered separately.
const BANNER_COLOR_OPTIONS = USERNAME_COLOR_OPTIONS.filter((o) => !isReserved(o.hex));

// Sidebar nav model. `soon: true` rows are dimmed, unclickable placeholders that
// advertise the shape of features we haven't built yet (Discord-style roadmap).
// The real sections are wired to content panes below; soon-sections render a
// "coming soon" stub so the layout reads complete without faking functionality.
const NAV_SECTIONS = [
  { id: 'profile', label: 'Profile', icon: ProfileIcon },
  { id: 'account', label: 'Account', icon: AccountIcon },
  { id: 'appearance', label: 'Appearance', icon: PaletteIcon },
  { id: 'voice', label: 'Voice & Video', icon: VideocamIcon },
  { id: 'notifications', label: 'Notifications', icon: NotificationsIcon },
  { id: 'messages', label: 'Messages', icon: MessagesIcon },
  { id: 'quickreplies', label: 'Quick Replies', icon: QuickReplyIcon },
  { id: 'privacy', label: 'Privacy & Safety', icon: PrivacyIcon, soon: true },
  { id: 'connections', label: 'Connections', icon: ConnectionsIcon, soon: true },
  { id: 'advanced', label: 'Advanced', icon: AdvancedIcon },
];

const useStyles = makeStyles()((theme) => ({
  // Wide two-pane dialog (sidebar + content), Discord-style.
  paper: {
    backgroundColor: theme.palette.background.paper,
    width: 860,
    maxWidth: '95vw',
    height: 600,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
  },
  // --- Left sidebar -------------------------------------------------------
  sidebar: {
    width: 220,
    flexShrink: 0,
    backgroundColor: theme.palette.custom.panel,
    borderRight: `1px solid ${theme.palette.custom.line}`,
    padding: theme.spacing(2.5, 1.5),
    overflowY: 'auto',
  },
  // The account identity header at the top of the sidebar.
  sidebarIdentity: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(2),
  },
  sidebarAvatar: {
    width: 36,
    height: 36,
  },
  // Avatars always paint their own pixels (photo, builtin, or the builtin
  // default fallback) — drop the colored fill behind them (used by both the
  // sidebar identity avatar and the Account section preview). '&&' doubles
  // the class specificity so this deterministically beats MUI's
  // .MuiAvatar-colorDefault regardless of emotion injection order (sx
  // overrides proved order-dependent).
  avatarPhoto: {
    '&&': { background: 'transparent' },
  },
  sidebarName: {
    fontWeight: 700,
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sidebarSub: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    width: '100%',
    padding: theme.spacing(0.85, 1.25),
    marginBottom: 2,
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    color: theme.palette.text.secondary,
    fontFamily: 'inherit',
    fontSize: '0.85rem',
    fontWeight: 600,
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'background 0.12s, color 0.12s',
    '&:hover': {
      backgroundColor: theme.palette.custom.hoverOverlay,
      color: theme.palette.text.primary,
    },
    '& svg': { fontSize: '1.1rem' },
  },
  navItemActive: {
    backgroundColor: theme.palette.custom.accentWash,
    color: theme.palette.text.primary,
  },
  // Dimmed, unclickable "coming soon" rows.
  navItemSoon: {
    cursor: 'default',
    opacity: 0.45,
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
    },
  },
  soonTag: {
    marginLeft: 'auto',
    fontSize: '0.6rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.custom.line}`,
    borderRadius: 4,
    padding: '1px 5px',
  },
  navDivider: {
    height: 1,
    backgroundColor: theme.palette.custom.line,
    margin: theme.spacing(1.5, 1),
  },
  // --- Right content pane -------------------------------------------------
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.custom.line}`,
    flexShrink: 0,
  },
  contentTitle: {
    fontWeight: 700,
    fontSize: '1.05rem',
  },
  closeBtn: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
  },
  contentBody: {
    padding: theme.spacing(3),
    overflowY: 'auto',
    flex: 1,
  },
  // Section heading inside a pane (e.g. "Account Info", "Theme").
  groupHeading: {
    fontWeight: 700,
    fontSize: '1rem',
    marginBottom: theme.spacing(2),
  },
  groupHeadingSpaced: {
    marginTop: theme.spacing(4),
  },
  sectionLabel: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  // Right-aligned live char counter under the profile bio editor.
  bioCounter: {
    display: 'block',
    textAlign: 'right',
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  // --- Avatar upload row --------------------------------------------------
  // A row with the current avatar preview + a change/upload control, sitting
  // above the account-info card in the My Account section.
  avatarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  avatarPreview: {
    width: 72,
    height: 72,
    fontSize: '1.6rem',
  },
  avatarControls: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  avatarButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  avatarHint: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  avatarError: {
    fontSize: '0.75rem',
    color: theme.palette.error.main,
  },
  // --- Quick Replies management pane ---------------------------------------
  // Header row: "Your snippets" label + the N/20 count chip.
  qrHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  qrCount: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.custom.line}`,
    borderRadius: 10,
    padding: '1px 8px',
  },
  // One draggable snippet row inside the sunken card.
  qrRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    '& + &': {
      borderTop: `1px solid ${theme.palette.custom.line}`,
    },
  },
  qrRowDragging: {
    opacity: 0.4,
  },
  qrDragHandle: {
    display: 'inline-flex',
    color: theme.palette.text.secondary,
    cursor: 'grab',
    flexShrink: 0,
    '& svg': { fontSize: '1.1rem' },
  },
  // The snippet text — click to edit inline.
  qrText: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    background: 'transparent',
    color: theme.palette.text.primary,
    fontFamily: 'inherit',
    fontSize: '0.85rem',
    textAlign: 'left',
    cursor: 'text',
    padding: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // "used N×" caption — analytics display ONLY (order stays manual).
  qrUses: {
    flexShrink: 0,
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  qrDeleteBtn: {
    color: theme.palette.text.secondary,
    padding: 2,
  },
  qrEmpty: {
    padding: theme.spacing(1.5, 0),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  qrAddRow: {
    marginTop: theme.spacing(2),
  },
  qrError: {
    fontSize: '0.75rem',
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
  // --- Account-info rows (label / value / Edit) ---------------------------
  // A sunken card holding the account field rows, like Discord's grouped panel.
  fieldCard: {
    backgroundColor: theme.palette.custom.panel,
    border: `1px solid ${theme.palette.custom.line}`,
    borderRadius: 8,
    padding: theme.spacing(0.5, 2),
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    '& + &': {
      borderTop: `1px solid ${theme.palette.custom.line}`,
    },
  },
  fieldLabel: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    width: 140,
    flexShrink: 0,
  },
  fieldValue: {
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fieldValueMuted: {
    color: theme.palette.text.secondary,
  },
  // Small disabled "Edit" button — affordance present, not yet wired.
  editBtn: {
    flexShrink: 0,
    fontSize: '0.75rem',
    padding: theme.spacing(0.25, 1.5),
    minWidth: 0,
  },
  roleChip: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.custom.accentContrastText,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 99,
    padding: '2px 10px',
  },
  // --- Appearance / Voice panes (lifted from the old Profile dialog) ------
  select: {
    minWidth: 220,
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  optionSwatch: {
    width: 16,
    height: 16,
    borderRadius: 3,
    flexShrink: 0,
    border: '1px solid rgba(255,255,255,0.25)',
    boxSizing: 'border-box',
  },
  guestNote: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  subSection: {
    marginTop: theme.spacing(3),
  },
  // --- Profile pane: two-column Discord-style layout ----------------------
  // Editable controls on the left, a live read-only preview on the right.
  // Collapses to a single column (preview under the controls) on narrow widths.
  profileGrid: {
    display: 'flex',
    gap: theme.spacing(4),
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(3),
    },
  },
  profileControls: {
    flex: '1 1 0',
    minWidth: 0,
  },
  // The preview column: sticky-ish card mirroring how the profile reads to
  // others. Framed so it reads as a preview surface, not another form.
  profilePreview: {
    flex: '0 0 300px',
    maxWidth: 300,
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
      maxWidth: 340,
      width: '100%',
    },
  },
  previewLabel: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5, 0.5, 1),
  },
  helpText: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    maxWidth: 460,
  },
  themeCards: {
    display: 'flex',
    gap: theme.spacing(1.5),
    maxWidth: 360,
  },
  themeCard: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    background: 'transparent',
    border: `2px solid ${theme.palette.custom.line}`,
    borderRadius: 10,
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    fontFamily: 'inherit',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: theme.palette.text.secondary,
      color: theme.palette.text.primary,
    },
  },
  themeCardActive: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.text.primary,
  },
  themeSwatch: {
    width: '100%',
    height: 38,
    borderRadius: 6,
    border: '1px solid rgba(255,255,255,0.18)',
    boxSizing: 'border-box',
  },
  themeLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  themeCheck: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.custom.accentContrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fontSize: '0.85rem',
    },
  },
  // "Coming soon" stub for dimmed sections (if ever navigated to).
  soonStub: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  // Sound dropdown + preview button on one line.
  soundRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // Volume slider — constrained width so it doesn't span the whole pane.
  volumeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    maxWidth: 280,
    paddingLeft: theme.spacing(0.5),
  },
  // Inline "Soon" badge next to a disabled option label.
  soonInline: {
    fontSize: '0.6rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.custom.line}`,
    borderRadius: 4,
    padding: '1px 5px',
    marginLeft: theme.spacing(0.5),
  },
  // --- Advanced / danger zone --------------------------------------------
  // A bordered card tinted with the error color, holding destructive actions.
  dangerCard: {
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: 8,
    padding: theme.spacing(2.5),
    maxWidth: 460,
  },
  dangerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(0.5),
    '& svg': { fontSize: '1.2rem' },
  },
  dangerDesc: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  // Outlined red button for the initial (reversible) danger action.
  dangerBtn: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&:hover': {
      borderColor: theme.palette.error.main,
      backgroundColor: 'rgba(255, 1, 66, 0.08)',
    },
  },
  // Confirmation dialog paper + filled (final, disabled) confirm button.
  confirmPaper: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3),
    maxWidth: 420,
  },
  confirmTitle: {
    fontWeight: 700,
    fontSize: '1.05rem',
    marginBottom: theme.spacing(1),
  },
  confirmText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  confirmActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  dangerConfirmBtn: {
    backgroundColor: theme.palette.error.main,
    color: '#fff',
    '&:hover': { backgroundColor: theme.palette.error.dark },
  },
  confirmNote: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1.5),
    textAlign: 'right',
  },
  // --- Stats section ------------------------------------------------------
  // Responsive grid of stat tiles; auto-fills as the pane widens.
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: theme.spacing(1.5),
    maxWidth: 560,
  },
  statTile: {
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1.1,
    color: theme.palette.text.primary,
  },
  statLabel: {
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  statsEmpty: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
}));

/**
 * Settings dialog — Discord-style two-pane layout (category sidebar + content
 * pane). Replaces the old single-column "Profile" dialog; same prop contract,
 * plus account-identity props for the new "My Account" section.
 *
 * Props:
 *  - open, onClose
 *  - user                 : { username, displayName, email, avatar } for the
 *                           Account section + sidebar identity header
 *  - role                 : membership role string (GUEST/USER/ADMIN/OWNER) — chip
 *  - isGuest              : guests see a register-to-edit notice instead of fields
 *  - username             : current display name (live color preview)
 *  - usernameColor        : current resolved color
 *  - canPick              : false for guests (color not user-editable)
 *  - canUseReserved       : true for ADMIN/OWNER (sees the reserved band)
 *  - onUsernameColorChange(hex)
 *  - outputQuality        : current webcam output quality (low|default|high)
 *  - canPickOutputQuality : false for guests (forced to default)
 *  - onOutputQualityChange(quality)
 *  - themeName            : current visual theme ('midnight' | 'sesh')
 *  - onThemeChange(name)  : apply + persist a theme; omit to hide the theme picker
 *  - mentionSound         : current mention-notify sound ('chime' | 'off')
 *  - onMentionSoundChange(value) : apply + persist the mention sound
 *  - imMode               : bool — AIM-style IM mode on/off (Messages section)
 *  - onImModeChange(on)   : toggle + persist IM mode
 *  - imClassic            : bool — retro AIM skin on/off (overrides app theme)
 *  - onImClassicChange(on): toggle + persist the classic IM skin
 *  - awayMessage          : current away message ('' = not away)
 *  - onAwayChange(text)   : set/clear the away message ('' clears it)
 *  - onAvatarChange(file) : upload + persist a new avatar; returns a Promise<bool>
 *                           (true = success). Omit/guest hides the avatar control.
 *  - rawAvatar            : the UNRESOLVED stored avatar value (e.g. a
 *                           "builtin:NN:hex" spec) — seeds the builtin picker
 *  - onAvatarPick(spec)   : persist a builtin avatar spec; returns Promise<bool>.
 *                           Omit/guest hides the "Pick avatar" control.
 *  - bio                  : current profile bio (per-tenant); '' = unset
 *  - onBioChange(text)    : persist the bio ('' clears it). Omit/guest hides it.
 *  - bannerColor          : current profile banner hex ('' = theme default)
 *  - onBannerColorChange(hex) : persist the banner color ('' clears it)
 *  - quickReplies         : [{ id, text, uses, createdAt }] — the user's saved
 *                           composer snippets (ACKED server list, array order =
 *                           user-chosen display order)
 *  - onQuickRepliesChange(nextList) : Promise — persist a full-list replace
 *                           (add/edit/delete/reorder); rejects on server error
 */
const ProfileDialog = ({
  open,
  onClose,
  user = {},
  role,
  isGuest = false,
  username,
  usernameColor,
  canPick = false,
  canUseReserved = false,
  onUsernameColorChange,
  outputQuality = 'default',
  canPickOutputQuality = false,
  onOutputQualityChange,
  themeName,
  onThemeChange,
  mentionSound = 'chime',
  onMentionSoundChange,
  mentionVolume = DEFAULT_MENTION_VOLUME,
  onMentionVolumeChange,
  onMentionVolumePreview,
  fontSize = DEFAULT_FONT_SIZE,
  onFontSizeChange,
  timestampFormat = DEFAULT_TIMESTAMP_FORMAT,
  onTimestampFormatChange,
  imMode = false,
  onImModeChange,
  imClassic = false,
  onImClassicChange,
  awayMessage = '',
  onAwayChange,
  deviceSetup = null,
  onDeviceSetupChange,
  micProcessing = null,
  onMicProcessingChange,
  isPublishing = false,
  onAvatarChange,
  rawAvatar,
  onAvatarPick,
  bio = '',
  onBioChange,
  bannerColor = '',
  onBannerColorChange,
  quickReplies = [],
  onQuickRepliesChange,
}) => {
  const { classes, cx } = useStyles();
  const [section, setSection] = useState('profile');
  // Avatar upload state. The hidden <input type=file> is triggered by the
  // "Change photo" button; avatarBusy disables it while the upload is in flight,
  // and avatarError surfaces a client-side rejection (size/type) inline.
  const avatarInputRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  // Builtin avatar & color picker (the legacy chat71 ColorAvatarPopup remake).
  const [pickerOpen, setPickerOpen] = useState(false);
  // Close-Account confirmation dialog (Advanced section). Inert final action.
  const [confirmClose, setConfirmClose] = useState(false);
  // Local mirror of the away message so the textarea stays responsive; committed
  // to the parent on blur. Re-synced from the prop whenever it changes upstream.
  const [awayDraft, setAwayDraft] = useState(awayMessage || '');
  useEffect(() => {
    setAwayDraft(awayMessage || '');
  }, [awayMessage]);

  // Local mirror of the profile bio so the textarea stays responsive; committed
  // to the parent on blur. Re-synced whenever the prop changes upstream.
  const [bioDraft, setBioDraft] = useState(bio || '');
  useEffect(() => {
    setBioDraft(bio || '');
  }, [bio]);

  // --- Quick Replies pane state ---------------------------------------------
  // qrDraft: the add-field text; qrEditingId/qrEditDraft: the row being edited
  // inline (click text → TextField); qrDragIndex: HTML5 drag-reorder source
  // row while a drag is in flight; qrBusy/qrError: the shared mutate() state.
  const [qrDraft, setQrDraft] = useState('');
  const [qrEditingId, setQrEditingId] = useState(null);
  const [qrEditDraft, setQrEditDraft] = useState('');
  const [qrDragIndex, setQrDragIndex] = useState(null);
  const [qrBusy, setQrBusy] = useState(false);
  const [qrError, setQrError] = useState('');

  // Reset to the first section each time the dialog reopens, so it always lands
  // on Profile rather than wherever the user last navigated. Also dismiss any
  // open confirm dialog so it never lingers across opens.
  useEffect(() => {
    if (open) {
      setSection('profile');
      setConfirmClose(false);
      setAvatarError('');
      // Quick Replies pane: never carry an in-flight edit/drag across opens.
      setQrEditingId(null);
      setQrDragIndex(null);
      setQrError('');
    }
  }, [open]);

  // Non-staff users never see reserved colors in the dropdown.
  const options = canUseReserved
    ? USERNAME_COLOR_OPTIONS
    : USERNAME_COLOR_OPTIONS.filter((o) => !isReserved(o.hex));

  // The current color may not be one of the named options (e.g. a role default
  // or a legacy value); the Select needs an exact value match, so fall back to
  // '' (nothing selected) rather than rendering an out-of-range warning.
  const current = (usernameColor || '').toLowerCase();
  const selectValue =
    options.find((o) => o.hex.toLowerCase() === current)?.hex || '';

  const handlePick = (hex) => {
    if (!canPick || !hex) return;
    onUsernameColorChange && onUsernameColorChange(hex);
  };

  // Client-side avatar guards mirror the backend (5 MB cap, image allowlist) so
  // an obvious reject is instant instead of a round-trip. The backend re-checks
  // by magic bytes regardless — this is just UX.
  const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
  const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  const handleAvatarPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Allow re-picking the same file later (onChange won't fire otherwise).
    e.target.value = '';
    if (!file || !onAvatarChange) return;

    setAvatarError('');
    if (!AVATAR_TYPES.includes(file.type)) {
      setAvatarError('Use a JPEG, PNG, GIF, or WEBP image.');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError('Image too large (max 5 MB).');
      return;
    }

    setAvatarBusy(true);
    try {
      const ok = await onAvatarChange(file);
      if (!ok) setAvatarError('Upload failed. Please try again.');
    } finally {
      setAvatarBusy(false);
    }
  };

  // Persist a builtin avatar choice. Faithful to the original ColorAvatarPopup
  // Submit, which dispatched both ChangeAvatarEvent AND ChangeColorEvent: the
  // picked color tints the avatar and becomes the username color in one step.
  const handleBuiltinAvatarSave = async ({ id, hex }) => {
    const spec = makeBuiltinAvatarSpec(id, hex);
    if (!spec || !onAvatarPick) return false;
    const ok = await onAvatarPick(spec);
    if (ok && canPick && onUsernameColorChange) {
      onUsernameColorChange(`#${hex}`);
    }
    return ok;
  };

  const displayName = user.displayName || user.username || username || 'You';
  const navSections = NAV_SECTIONS;
  const activeNav = navSections.find((s) => s.id === section) || navSections[0];

  // A read-only account field row with a disabled action affordance (the flows
  // aren't wired to the backend yet — the buttons advertise what's coming). The
  // button label defaults to "Edit"; pass actionLabel to vary it (e.g. "Manage").
  const FieldRow = ({
    label,
    value,
    muted,
    editable = true,
    actionLabel = 'Edit',
    children,
  }) => (
    <div className={classes.fieldRow}>
      <span className={classes.fieldLabel}>{label}</span>
      <span
        className={cx(classes.fieldValue, muted && classes.fieldValueMuted)}
      >
        {children || value}
      </span>
      {editable && (
        <Button
          className={classes.editBtn}
          variant="outlined"
          size="small"
          disabled
          title={`${actionLabel} — coming soon`}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );

  // Profile pane — presentation settings (avatar, username color, bio, banner
  // color) in a two-column layout: editable controls on the left, a live
  // read-only preview (the shared ProfileView, same component that renders other
  // users' profiles) on the right that tracks every change as you make it.
  const renderProfile = () => {
    const controls = (
      <div className={classes.profileControls}>
        {!isGuest && onAvatarChange && (
          <>
            <Typography className={classes.sectionLabel}>Avatar</Typography>
            <div className={classes.avatarRow}>
              <Avatar
                src={user.avatar}
                alt={displayName}
                className={cx(classes.avatarPreview, classes.avatarPhoto)}
              >
                <AvatarFallback letter={displayName.charAt(0).toUpperCase()} />
              </Avatar>
              <div className={classes.avatarControls}>
                <div className={classes.avatarButtons}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={avatarBusy}
                    onClick={() => avatarInputRef.current?.click()}
                  >
                    {avatarBusy ? 'Uploading…' : 'Change photo'}
                  </Button>
                  {onAvatarPick && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setPickerOpen(true)}
                    >
                      Pick avatar
                    </Button>
                  )}
                </div>
                {avatarError ? (
                  <Typography className={classes.avatarError}>
                    {avatarError}
                  </Typography>
                ) : (
                  <Typography className={classes.avatarHint}>
                    Upload a photo (JPEG, PNG, GIF, or WEBP · up to 5 MB) or
                    pick a classic avatar in your color
                  </Typography>
                )}
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleAvatarPick}
              />
            </div>
          </>
        )}

        <Typography
          className={cx(classes.sectionLabel, classes.groupHeadingSpaced)}
        >
          Username color
        </Typography>
        {canPick ? (
          <Select
            className={classes.select}
            variant="outlined"
            margin="dense"
            displayEmpty
            value={selectValue}
            onChange={(e) => handlePick(e.target.value)}
            renderValue={(val) => {
              const opt = options.find((o) => o.hex === val);
              return (
                <span className={classes.optionRow}>
                  <span
                    className={classes.optionSwatch}
                    style={{
                      backgroundColor: opt
                        ? opt.hex
                        : usernameColor || 'transparent',
                    }}
                  />
                  <span style={{ color: opt ? opt.hex : usernameColor || undefined }}>
                    {opt ? opt.name : 'Custom'}
                  </span>
                </span>
              );
            }}
          >
            {options.map((o) => (
              <MenuItem key={o.hex} value={o.hex}>
                <span className={classes.optionRow}>
                  <span
                    className={classes.optionSwatch}
                    style={{ backgroundColor: o.hex }}
                  />
                  <span style={{ color: o.hex }}>{o.name}</span>
                </span>
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Typography className={classes.guestNote}>
            Guests use the default username color. Register an account to choose
            your own.
          </Typography>
        )}

        {!isGuest && onBioChange && (
          <>
            <Typography
              className={cx(classes.sectionLabel, classes.groupHeadingSpaced)}
            >
              Bio
            </Typography>
            <TextField
              value={bioDraft}
              onChange={(e) => setBioDraft(e.target.value.slice(0, PROFILE_BIO_MAX))}
              onBlur={() => {
                const next = bioDraft.slice(0, PROFILE_BIO_MAX);
                if (next !== (bio || '')) onBioChange(next);
              }}
              placeholder="Tell the room a little about you"
              multiline
              minRows={2}
              maxRows={5}
              fullWidth
              variant="outlined"
              margin="dense"
              slotProps={{ htmlInput: { maxLength: PROFILE_BIO_MAX } }}
            />
            <Typography className={classes.bioCounter}>
              {bioDraft.length}/{PROFILE_BIO_MAX}
            </Typography>
          </>
        )}

        {!isGuest && onBannerColorChange && (
          <>
            <Typography
              className={cx(classes.sectionLabel, classes.groupHeadingSpaced)}
            >
              Banner color
            </Typography>
            <Select
              className={classes.select}
              variant="outlined"
              margin="dense"
              displayEmpty
              value={(bannerColor || '').toLowerCase()}
              onChange={(e) => onBannerColorChange(e.target.value)}
              renderValue={(val) => {
                const opt = BANNER_COLOR_OPTIONS.find((o) => o.hex.toLowerCase() === val);
                return (
                  <span className={classes.optionRow}>
                    <span
                      className={classes.optionSwatch}
                      style={{ backgroundColor: val || 'transparent' }}
                    />
                    <span>{opt ? opt.name : 'Theme default'}</span>
                  </span>
                );
              }}
            >
              <MenuItem value="">
                <span className={classes.optionRow}>
                  <span className={classes.optionSwatch} style={{ backgroundColor: 'transparent' }} />
                  <span>Theme default</span>
                </span>
              </MenuItem>
              {BANNER_COLOR_OPTIONS.map((o) => (
                <MenuItem key={o.hex} value={o.hex.toLowerCase()}>
                  <span className={classes.optionRow}>
                    <span className={classes.optionSwatch} style={{ backgroundColor: o.hex }} />
                    <span>{o.name}</span>
                  </span>
                </MenuItem>
              ))}
            </Select>
          </>
        )}
      </div>
    );

    // Live preview — mirrors the in-progress draft values. usernameColor,
    // bannerColor and user.avatar are live props; bioDraft is live local state,
    // so the preview updates as the user edits each control.
    const preview = (
      <div className={classes.profilePreview}>
        <Typography className={classes.previewLabel}>Preview</Typography>
        <ProfileView
          profile={{
            username: displayName,
            usernameColor,
            role: isGuest ? 'guest' : role,
            avatarUrl: user.avatar,
            bannerColor,
            bio: bioDraft,
            country: user.country,
            memberSince: user.memberSince,
          }}
          editable={false}
        />
      </div>
    );

    return (
      <div className={classes.profileGrid}>
        {controls}
        {preview}
      </div>
    );
  };

  const renderAccount = () => (
    <>
      <Typography className={classes.groupHeading}>Account Info</Typography>
      {isGuest ? (
        <Typography className={classes.guestNote}>
          You’re browsing as a guest. Register an account to set a username,
          email, and password you can manage here.
        </Typography>
      ) : (
        <div className={classes.fieldCard}>
          <FieldRow label="Username" value={displayName} />
          <FieldRow
            label="Email"
            value={user.email || 'Not set'}
            muted={!user.email}
          />
          <FieldRow label="Account Type" editable={false}>
            <span className={classes.roleChip}>{role || 'USER'}</span>
          </FieldRow>
        </div>
      )}

      <Typography
        className={cx(classes.groupHeading, classes.groupHeadingSpaced)}
      >
        Password & Security
      </Typography>
      <div className={classes.fieldCard}>
        <FieldRow label="Password" editable={!isGuest}>
          <span className={classes.fieldValueMuted}>••••••••</span>
        </FieldRow>
        <FieldRow
          label="Logged-in Devices"
          editable={!isGuest}
          actionLabel="Manage"
        >
          <span className={classes.fieldValueMuted}>
            Manage your active sessions
          </span>
        </FieldRow>
        <FieldRow
          label="Two-factor Authentication"
          editable={!isGuest}
          actionLabel="Enable"
        >
          <span className={classes.fieldValueMuted}>Not enabled</span>
        </FieldRow>
      </div>
    </>
  );

  const renderAppearance = () => (
    <>
      <Typography className={classes.groupHeading}>Appearance</Typography>

      {/* Theme — per-user appearance preference. Swatch cards tinted with each
          theme's accent; the active one is ringed + checkmarked. onThemeChange
          applies and persists the choice, so the switch is instant and survives
          reloads. */}
      {onThemeChange && (
        <div className={classes.subSection}>
          <Typography className={classes.sectionLabel}>Theme</Typography>
          <div className={classes.themeCards} role="group" aria-label="Theme">
            {Object.entries(themeMeta).map(([name, meta]) => {
              const active = themeName === name;
              return (
                <button
                  key={name}
                  type="button"
                  className={cx(classes.themeCard, active && classes.themeCardActive)}
                  onClick={() => onThemeChange(name)}
                  aria-pressed={active}
                  title={`${meta.label} theme`}
                >
                  {active && (
                    <span className={classes.themeCheck}>
                      <CheckIcon />
                    </span>
                  )}
                  <span
                    className={classes.themeSwatch}
                    style={{ backgroundColor: meta.accent }}
                  />
                  <span className={classes.themeLabel}>{meta.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Chat display — message text size + timestamp format. Per-viewer reading
          prefs that apply to the whole transcript (like view mode). */}
      <div className={classes.subSection}>
        <Typography className={classes.sectionLabel}>Message font size</Typography>
        <Select
          className={classes.select}
          variant="outlined"
          margin="dense"
          value={FONT_SIZES.includes(fontSize) ? fontSize : DEFAULT_FONT_SIZE}
          onChange={(e) => onFontSizeChange && onFontSizeChange(e.target.value)}
        >
          {FONT_SIZES.map((pct) => (
            <MenuItem key={pct} value={pct}>
              {pct}%
            </MenuItem>
          ))}
        </Select>
      </div>

      <div className={classes.subSection}>
        <Typography className={classes.sectionLabel}>Message timestamps</Typography>
        <RadioGroup
          value={TIMESTAMP_FORMATS.includes(timestampFormat) ? timestampFormat : DEFAULT_TIMESTAMP_FORMAT}
          onChange={(e) => onTimestampFormatChange && onTimestampFormatChange(e.target.value)}
        >
          <FormControlLabel
            value="clock"
            control={<Radio color="primary" size="small" />}
            label="Clock time (2:34 PM)"
          />
          <FormControlLabel
            value="relative"
            control={<Radio color="primary" size="small" />}
            label="Relative (2m ago)"
          />
          <FormControlLabel
            value="off"
            control={<Radio color="primary" size="small" />}
            label="Off"
          />
        </RadioGroup>
        <Typography className={classes.helpText}>
          With timestamps off, the time still shows when you hover a message
          (or press and hold on touch).
        </Typography>
      </div>
    </>
  );

  const renderVoice = () => (
    <>
      <Typography className={classes.groupHeading}>Voice &amp; Video</Typography>

      {/* Camera + microphone device selection and start-on-join toggles — the
          same controls as the pre-join setup modal, minus the video preview.
          Changes persist (and, if you're broadcasting, restart the publish with
          the new device). Shown only when the parent wires onDeviceSetupChange. */}
      {onDeviceSetupChange && (
        <div className={classes.subSection}>
          <Typography className={classes.sectionLabel}>Camera &amp; microphone</Typography>
          <DeviceSettingsPanel
            initialSettings={deviceSetup}
            onConfirm={onDeviceSetupChange}
            isGuest={isGuest}
          />
        </div>
      )}

      {/* The vocal chain: gain / low-cut / compressor / gate + live meter.
          While this pane is open and you're not broadcasting, a temporary
          mic-only capture feeds the meter so the chain can be tuned dry-run;
          it's released when the pane closes. */}
      {micProcessing && onMicProcessingChange && (
        <div className={classes.subSection}>
          <Typography className={classes.sectionLabel}>Microphone processing</Typography>
          <MicProcessingPanel
            settings={micProcessing}
            onChange={onMicProcessingChange}
            isPublishing={isPublishing}
            savedAudioDeviceId={deviceSetup?.audioDeviceId || ''}
          />
        </div>
      )}

      {/* Webcam output quality — the max layer the publisher emits (viewers see
          min(their_choice, this)). Members only; guests output at the default
          quality with no option. */}
      {canPickOutputQuality ? (
        <>
          <Typography className={classes.sectionLabel}>
            Webcam output quality
          </Typography>
          <Select
            className={classes.select}
            variant="outlined"
            margin="dense"
            value={outputQuality}
            onChange={(e) =>
              onOutputQualityChange && onOutputQualityChange(e.target.value)
            }
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </Select>
          <Typography className={classes.helpText}>
            The maximum quality your webcam broadcasts. Viewers on slower
            connections may still receive a lower layer.
          </Typography>
        </>
      ) : (
        <Typography className={classes.guestNote}>
          Guests broadcast at the default quality. Register an account to choose
          your webcam output quality.
        </Typography>
      )}
    </>
  );

  // Notifications pane. Master toggle (mentionSound 'off' vs a sound id) plus a
  // sound picker with a preview button. The "who can ping me" choice is shown
  // but disabled — it needs a friends backend that doesn't exist yet.
  const notifyEnabled = mentionSound !== 'off';
  // The sound the picker shows / restores to when notifications are toggled back
  // on. Defaults to 'chime'; remembers the last non-off choice this session.
  const [lastSound, setLastSound] = useState(
    mentionSound !== 'off' ? mentionSound : 'chime',
  );
  useEffect(() => {
    if (mentionSound !== 'off') setLastSound(mentionSound);
  }, [mentionSound]);

  const handleNotifyToggle = (on) => {
    onMentionSoundChange && onMentionSoundChange(on ? lastSound : 'off');
  };

  const renderNotifications = () => (
    <>
      <Typography className={classes.groupHeading}>Notify me when…</Typography>
      <div className={classes.fieldCard}>
        <div className={classes.fieldRow}>
          <span className={classes.fieldValue}>
            A friend comes online
            <span className={classes.soonInline}>Soon</span>
          </span>
          <Switch checked={false} disabled color="primary" />
        </div>
      </div>

      <Typography
        className={cx(classes.groupHeading, classes.groupHeadingSpaced)}
      >
        Mentions
      </Typography>

      <div className={classes.fieldCard}>
        <div className={classes.fieldRow}>
          <span className={classes.fieldValue}>
            Play a sound when someone @mentions me
          </span>
          <Switch
            checked={notifyEnabled}
            onChange={(e) => handleNotifyToggle(e.target.checked)}
            color="primary"
          />
        </div>
      </div>

      {/* Sound picker + preview — only meaningful while notifications are on. */}
      <div className={classes.subSection}>
        <Typography className={classes.sectionLabel}>Notification sound</Typography>
        <div className={classes.soundRow}>
          <Select
            className={classes.select}
            variant="outlined"
            margin="dense"
            value={notifyEnabled ? mentionSound : lastSound}
            disabled={!notifyEnabled}
            onChange={(e) => onMentionSoundChange && onMentionSoundChange(e.target.value)}
          >
            <MenuItem value="chime">Chime</MenuItem>
          </Select>
          <IconButton
            size="small"
            disabled={!notifyEnabled}
            onClick={() => playMentionSound(notifyEnabled ? mentionSound : lastSound, mentionVolume)}
            title="Preview sound"
            aria-label="Preview notification sound"
          >
            <PlayIcon fontSize="small" />
          </IconButton>
        </div>
        <Typography className={classes.helpText}>
          More sounds are coming soon.
        </Typography>
      </div>

      {/* Volume — scales the notification chime. Disabled when sound is off.
          Committed on release (onChangeCommitted) so we persist/preview once,
          not on every drag tick; the live value updates as you drag. */}
      <div className={classes.subSection}>
        <Typography className={classes.sectionLabel}>Notification volume</Typography>
        <div className={classes.volumeRow}>
          <Slider
            value={typeof mentionVolume === 'number' ? mentionVolume : DEFAULT_MENTION_VOLUME}
            min={0}
            max={100}
            step={5}
            disabled={!notifyEnabled}
            valueLabelDisplay="auto"
            aria-label="Notification volume"
            onChange={(e, val) =>
              onMentionVolumeChange && onMentionVolumeChange(Array.isArray(val) ? val[0] : val)
            }
            onChangeCommitted={() => onMentionVolumePreview && onMentionVolumePreview()}
          />
        </div>
      </div>

      {/* Who can ping me — present but disabled until a friends system exists. */}
      <div className={classes.subSection}>
        <Typography className={classes.sectionLabel}>Notify me from</Typography>
        <RadioGroup value="all">
          <FormControlLabel
            value="all"
            control={<Radio color="primary" size="small" disabled={!notifyEnabled} />}
            label="Anyone in the room"
          />
          <FormControlLabel
            value="friends"
            control={<Radio size="small" disabled />}
            label={
              <span>
                Friends only <span className={classes.soonInline}>Soon</span>
              </span>
            }
          />
        </RadioGroup>
      </div>
    </>
  );

  // Messages — IM mode (AIM-style private chat) toggle + away message. The away
  // field is committed on blur (not per keystroke) so we don't relay a socket
  // event on every character; a local mirror keeps the input responsive.
  const renderMessages = () => (
    <>
      <Typography className={classes.groupHeading}>Private messages</Typography>
      <div className={classes.fieldCard}>
        <div className={classes.fieldRow}>
          <span className={classes.fieldValue}>
            IM mode (AOL-style chat windows)
          </span>
          <Switch
            checked={!!imMode}
            onChange={(e) => onImModeChange && onImModeChange(e.target.checked)}
            color="primary"
          />
        </div>
      </div>
      <Typography className={classes.helpText}>
        When on, private chats open in their own draggable IM window — classic
        Instant Messenger style. When off, private messages show up inline in the
        main chat.
      </Typography>

      <div className={classes.fieldCard}>
        <div className={classes.fieldRow}>
          <span className={classes.fieldValue}>Retro skin (Y2K Instant Messenger)</span>
          <Switch
            checked={!!imClassic}
            onChange={(e) => onImClassicChange && onImClassicChange(e.target.checked)}
            color="primary"
          />
        </div>
      </div>
      <Typography className={classes.helpText}>
        Give your IM windows the old-school Instant Messenger look — blue title
        bar, white message pane, and a beige toolbar. Overrides the app theme for
        IM windows only.
      </Typography>

      <div className={classes.subSection}>
        <Typography className={classes.sectionLabel}>Away message</Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          variant="outlined"
          size="small"
          placeholder="e.g. Away from keyboard — back soon!"
          value={awayDraft}
          onChange={(e) => setAwayDraft(e.target.value.slice(0, 500))}
          onBlur={() => {
            if (awayDraft !== awayMessage) onAwayChange && onAwayChange(awayDraft);
          }}
          inputProps={{ maxLength: 500 }}
        />
        <Typography className={classes.helpText}>
          {awayDraft.trim()
            ? 'You appear away. Anyone who messages you privately gets this auto-reply once. Clear it to come back.'
            : 'Set an away message to auto-reply to private messages while you’re gone.'}
        </Typography>
      </div>
    </>
  );

  // --- Quick Replies management pane ---------------------------------------
  // Full CRUD + drag-reorder over the user's saved composer snippets. Every
  // mutation is a full-list replace through onQuickRepliesChange; render always
  // comes back from the ACKED server list (the quickReplies prop). The list
  // order is user-controlled — never derived from the use counters.
  const qrMutate = (next) => {
    if (!onQuickRepliesChange) return;
    setQrError('');
    setQrBusy(true);
    onQuickRepliesChange(next)
      .catch((e) => setQrError(e?.message || 'Could not save'))
      .finally(() => setQrBusy(false));
  };

  const qrAdd = () => {
    const text = qrDraft.trim();
    if (!text || qrBusy || quickReplies.length >= QUICK_REPLY_MAX) return;
    if (quickReplies.some((r) => r.text === text)) {
      setQrError('Already saved as a quick reply');
      return;
    }
    qrMutate([...quickReplies, { text }]);
    setQrDraft('');
  };

  // Commit an inline edit (Enter/blur). Empty or unchanged edits just cancel;
  // a dupe of ANOTHER row surfaces inline and keeps the editor open.
  const qrCommitEdit = (reply) => {
    const text = qrEditDraft.trim();
    if (!text || text === reply.text) {
      setQrEditingId(null);
      return;
    }
    if (quickReplies.some((r) => r.id !== reply.id && r.text === text)) {
      setQrError('Already saved as a quick reply');
      return;
    }
    qrMutate(quickReplies.map((r) => (r.id === reply.id ? { ...r, text } : r)));
    setQrEditingId(null);
  };

  const qrDropOn = (i) => {
    if (qrDragIndex === null || qrDragIndex === i) {
      setQrDragIndex(null);
      return;
    }
    const next = [...quickReplies];
    const [moved] = next.splice(qrDragIndex, 1);
    next.splice(i, 0, moved);
    setQrDragIndex(null);
    qrMutate(next); // one quickreply:set for the whole reorder
  };

  const renderQuickReplies = () => {
    const atCap = quickReplies.length >= QUICK_REPLY_MAX;
    return (
      <>
        <div className={classes.qrHeader}>
          <Typography className={classes.groupHeading} style={{ marginBottom: 0 }}>
            Quick Replies
          </Typography>
          <span className={classes.qrCount}>
            {quickReplies.length}/{QUICK_REPLY_MAX}
          </span>
        </div>
        <Typography className={classes.helpText}>
          Saved snippets for the message box — insert one by typing / in chat or
          from the toolbar button. Drag to reorder; click a snippet to edit it.
        </Typography>

        <div className={classes.fieldCard}>
          {quickReplies.length === 0 && (
            <div className={classes.qrEmpty}>
              No quick replies yet — add your first one below.
            </div>
          )}
          {quickReplies.map((reply, i) => (
            <div
              key={reply.id}
              className={cx(
                classes.qrRow,
                qrDragIndex === i && classes.qrRowDragging
              )}
              draggable={qrEditingId !== reply.id}
              onDragStart={() => setQrDragIndex(i)}
              onDragOver={(e) => e.preventDefault()} // required to allow drop
              onDrop={() => qrDropOn(i)}
              onDragEnd={() => setQrDragIndex(null)}
            >
              <span className={classes.qrDragHandle} title="Drag to reorder">
                <DragIcon />
              </span>
              {qrEditingId === reply.id ? (
                <TextField
                  fullWidth
                  autoFocus
                  size="small"
                  variant="outlined"
                  value={qrEditDraft}
                  helperText={`${qrEditDraft.length}/${QUICK_REPLY_MAX_LEN}`}
                  slotProps={{ htmlInput: { maxLength: QUICK_REPLY_MAX_LEN } }}
                  onChange={(e) => {
                    setQrEditDraft(e.target.value);
                    if (qrError) setQrError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      qrCommitEdit(reply);
                    } else if (e.key === 'Escape') {
                      setQrEditingId(null);
                    }
                  }}
                  onBlur={() => qrCommitEdit(reply)}
                />
              ) : (
                <>
                  <button
                    type="button"
                    className={classes.qrText}
                    title={`${reply.text} — click to edit`}
                    onClick={() => {
                      setQrEditingId(reply.id);
                      setQrEditDraft(reply.text);
                      setQrError('');
                    }}
                  >
                    {reply.text}
                  </button>
                  {reply.uses > 0 && (
                    <Typography variant="caption" className={classes.qrUses}>
                      used {reply.uses}&times;
                    </Typography>
                  )}
                </>
              )}
              <IconButton
                className={classes.qrDeleteBtn}
                size="small"
                disabled={qrBusy}
                title="Delete quick reply"
                aria-label={`Delete quick reply: ${reply.text}`}
                onClick={() => qrMutate(quickReplies.filter((r) => r.id !== reply.id))}
              >
                <CloseIcon style={{ fontSize: '1rem' }} />
              </IconButton>
            </div>
          ))}
        </div>

        <TextField
          className={classes.qrAddRow}
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Add a quick reply…"
          value={qrDraft}
          disabled={qrBusy || atCap}
          helperText={
            atCap
              ? `${QUICK_REPLY_MAX}/${QUICK_REPLY_MAX} — remove one first`
              : `${qrDraft.length}/${QUICK_REPLY_MAX_LEN}`
          }
          slotProps={{ htmlInput: { maxLength: QUICK_REPLY_MAX_LEN } }}
          onChange={(e) => {
            setQrDraft(e.target.value);
            if (qrError) setQrError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              qrAdd();
            }
          }}
        />
        {qrError && <div className={classes.qrError}>{qrError}</div>}
      </>
    );
  };

  // Advanced — currently just the danger zone (Close Account). The confirm
  // dialog is fully built but its final action is inert (no backend yet).
  const renderAdvanced = () => (
    <>
      <Typography className={classes.groupHeading}>Advanced</Typography>
      {isGuest ? (
        <Typography className={classes.guestNote}>
          You’re browsing as a guest, so there’s no account to close. Register an
          account to manage it here.
        </Typography>
      ) : (
        <div className={classes.dangerCard}>
          <Typography className={classes.dangerTitle}>
            <WarningIcon /> Close Account
          </Typography>
          <Typography className={classes.dangerDesc}>
            Permanently close your account. This can’t be undone — your profile,
            messages, and settings will be removed.
          </Typography>
          <Button
            className={classes.dangerBtn}
            variant="outlined"
            size="small"
            onClick={() => setConfirmClose(true)}
          >
            Close Account
          </Button>
        </div>
      )}
    </>
  );

  const renderSoon = () => (
    <Typography className={classes.soonStub}>
      {activeNav.label} settings are coming soon.
    </Typography>
  );

  const renderSection = () => {
    switch (section) {
      case 'profile':
        return renderProfile();
      case 'account':
        return renderAccount();
      case 'appearance':
        return renderAppearance();
      case 'voice':
        return renderVoice();
      case 'notifications':
        return renderNotifications();
      case 'messages':
        return renderMessages();
      case 'quickreplies':
        return renderQuickReplies();
      case 'advanced':
        return renderAdvanced();
      default:
        return renderSoon();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }} maxWidth={false}>
      {/* Left sidebar — identity header + category nav */}
      <div className={classes.sidebar}>
        <div className={classes.sidebarIdentity}>
          <Avatar
            src={user.avatar}
            alt={displayName}
            className={cx(classes.sidebarAvatar, classes.avatarPhoto)}
          >
            <AvatarFallback letter={displayName.charAt(0).toUpperCase()} />
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <div className={classes.sidebarName}>{displayName}</div>
            <div className={classes.sidebarSub}>
              {isGuest ? 'Guest' : (role || 'USER')}
            </div>
          </div>
        </div>

        {navSections.map((s, i) => {
          const Icon = s.icon;
          const active = s.id === section;
          // Draw a hairline divider wherever the "soon" grouping flips — i.e.
          // before the first placeholder row, and again before a real row that
          // follows the placeholders (the Advanced danger zone).
          const prevSoon = i > 0 ? !!navSections[i - 1].soon : !!s.soon;
          const showDivider = i > 0 && !!s.soon !== prevSoon;
          return (
            <React.Fragment key={s.id}>
              {showDivider && <div className={classes.navDivider} />}
              <button
                type="button"
                className={cx(
                  classes.navItem,
                  active && classes.navItemActive,
                  s.soon && classes.navItemSoon,
                )}
                onClick={() => !s.soon && setSection(s.id)}
                aria-current={active ? 'page' : undefined}
                aria-disabled={s.soon || undefined}
                tabIndex={s.soon ? -1 : 0}
              >
                <Icon />
                <span>{s.label}</span>
                {s.soon && <span className={classes.soonTag}>Soon</span>}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Right content pane */}
      <div className={classes.content}>
        <div className={classes.contentHeader}>
          <Typography className={classes.contentTitle}>
            {activeNav.label}
          </Typography>
          <IconButton
            className={classes.closeBtn}
            size="small"
            onClick={onClose}
            aria-label="Close settings"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.contentBody}>{renderSection()}</div>
      </div>

      {/* Close-Account confirmation. The UX is complete but the final confirm
          is disabled — the backend delete flow isn't wired yet. */}
      <Dialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        classes={{ paper: classes.confirmPaper }}
      >
        <Typography className={classes.confirmTitle}>
          Close your account?
        </Typography>
        <Typography className={classes.confirmText}>
          This permanently removes your account and can’t be undone. You’ll be
          signed out and won’t be able to recover your profile or messages.
        </Typography>
        <div className={classes.confirmActions}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setConfirmClose(false)}
          >
            Cancel
          </Button>
          <Button
            className={classes.dangerConfirmBtn}
            variant="contained"
            size="small"
            disableElevation
            disabled
            title="Account closure is coming soon"
          >
            Close Account
          </Button>
        </div>
        <Typography className={classes.confirmNote}>
          Account closure is coming soon.
        </Typography>
      </Dialog>

      {/* Builtin avatar & identity-color picker (chat71 ColorAvatarPopup
          remake). Saving sets both the avatar and the username color. */}
      <AvatarPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        username={displayName}
        initialSpec={rawAvatar}
        initialColor={usernameColor}
        blockedColors={canUseReserved ? [] : RESERVED_COLORS}
        canUseStaffAvatars={canUseReserved}
        onSave={handleBuiltinAvatarSave}
      />
    </Dialog>
  );
};

export default ProfileDialog;
